import { defaults } from 'es-toolkit/compat';
import {
  createContext,
  useContext,
  useState,
} from 'react';
import type { ParsedError } from 'shared/api/errorMessage.js';
import { getErrorMessage } from 'shared/api/errorMessage.js';
import type { SyncResult } from 'shared/utils/type.utils.js';

type ErrorPayload = { error: any; updatedAt?: number };

type ErrorState = {
  error: { error: Error; updatedAt: number };
  parsedError: ParsedError;
} | {
  error: { error: null; updatedAt: number };
  parsedError: null;
};

type GlobalErrorContextValue = ErrorState & {
  setError: (error: ErrorPayload) => void;
  clearError: () => void;
};

const noErrorState: ErrorState = {
  error: { error: null, updatedAt: 0 },
  parsedError: null,
};

export const GlobalErrorContext = createContext<GlobalErrorContextValue>({
  ...noErrorState,
  setError: () => {},
  clearError: () => {},
});

export const useGlobalErrorContext = () => useContext(GlobalErrorContext);

/** Use once in app to create <ErrorContext value={} /> */
export const useGlobalErrorContextProvider = () => {
  const [state, setState] = useState<ErrorState>({
    error: { updatedAt: 0, error: null },
    parsedError: null,
  });

  const errorContextValue: GlobalErrorContextValue = {
    ...state,
    setError: (error: ErrorPayload) => {
      if (error === null) {
        return setState(noErrorState);
      }

      const errorWithDefaults = defaults(error, {
        updatedAt: Date.now(),
        parsedError: getErrorMessage(error),
      });

      if (!error) {
        return setState(errorWithDefaults);
      }

      if ((error.updatedAt ?? Date.now()) < (error.updatedAt ?? 0)) {
        return;
      }

      return setState(errorWithDefaults);
    },
    clearError: () => {
      setState(noErrorState);
    },
  };

  return errorContextValue;
};
